import {
  ExportFilterInputDto,
  FilterInputDto,
  FilterRule,
} from "./service-proxies";
import { GetListParams, GetManyReferenceParams } from "react-admin";

type ExportParams = Partial<GetListParams> & { exportFormat?: string };

interface ParseFilterProps {
  filter: any;
  initialFilter?: FilterRule;
}

const _parseFilter = ({
  filter,
  initialFilter,
}: ParseFilterProps): FilterRule => {
  if (!filter) {
    return {};
  }

  if (Object.keys(filter).length === 0 && filter.constructor === Object) {
    return initialFilter ?? {};
  }

  const filterProps = Object.getOwnPropertyNames(filter);
  if (filterProps.length === 1) {
    const filterPropName = filterProps[0];
    if (filterPropName === "q") {
      return initialFilter ? { ...initialFilter } : {};
    }

    if (filterPropName === "_not") {
      const retVal: FilterRule = initialFilter ? { ...initialFilter } : {};
      retVal.condition = "and";
      retVal.filterRules = (filter[filterPropName] as Array<{
        field: string;
        value: any;
      }>).map((current) => ({ ...current, operator: "not_equals" }));
      return retVal;
    }

    return initialFilter
      ? {
          ...initialFilter,
          condition: "and",
          filterRules: [
            {
              field: filterPropName,
              operator: "equals",
              value: filter[filterPropName],
            },
          ],
        }
      : {
          field: filterPropName,
          operator: "equals",
          value: filter[filterPropName],
        };
  }

  const rootRule: FilterRule = initialFilter
    ? {
        ...initialFilter,
        condition: "and",
        filterRules: filterProps
          .filter((x) => x !== "q" && x !== "_not")
          .map((filterPropName) => ({
            field: filterPropName,
            operator: "equals",
            value: filter[filterPropName],
          })),
      }
    : {
        condition: "and",
        filterRules: filterProps
          .filter((x) => x !== "q" && x !== "_not")
          .map((filterPropName) => ({
            field: filterPropName,
            operator: "equals",
            value: filter[filterPropName],
          })),
      };

  const notConditions = filterProps.find((x) => x === "_not");
  if (notConditions) {
    rootRule.filterRules = [
      ...rootRule.filterRules!,
      ...(filter["_not"] as Array<{
        field: string;
        value: any;
      }>).map((current) => ({ ...current, operator: "not_equals" })),
    ];
  }

  return rootRule;
};

const mapGetListParams = (
  params: GetListParams,
  searchFields?: Array<string>
): FilterInputDto => {
  const safeSearchFields = searchFields ?? [];
  const filterRule = _parseFilter({ filter: params.filter });
  const searchRule = params.filter["q"] && {
    columns: [...safeSearchFields],
    text: params.filter["q"],
  };
  return {
    sorting: `${params.sort?.field} ${params.sort?.order}`,
    skipCount:
      params.pagination.page > 0
        ? (params.pagination.page - 1) * params.pagination.perPage
        : 0,
    maxResultCount: params.pagination.perPage,
    filterRule,
    searchRule,
  };
};

export default {
  mapGetListParams,

  mapGetManyReferenceParams: (
    params: GetManyReferenceParams
  ): FilterInputDto => {
    const filterRule = _parseFilter({
      filter: params.filter,
      initialFilter: {
        field: params.target,
        value: `${params.id}`,
        operator: "equals",
      },
    });
    return {
      sorting: `${params.sort?.field} ${params.sort?.order}`,
      skipCount:
        params.pagination.page > 0
          ? (params.pagination.page - 1) * params.pagination.perPage
          : 0,
      maxResultCount: params.pagination.perPage,
      filterRule: filterRule,
    };
  },

  mapExportParams: (
    params: ExportParams,
    searchFields?: Array<string>
  ): ExportFilterInputDto => {
    const pagination: GetListParams["pagination"] = params.pagination ?? {
      page: 1,
      perPage: 1000,
    };

    const baseParams = mapGetListParams(
      {
        sort: params.sort ?? { field: "id", order: "DESC" },
        filter: params.filter ?? {},
        pagination,
      },
      searchFields
    );

    return {
      ...baseParams,
      exportFormat: params.exportFormat,
    };
  },
};
